import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import Grid from '@mui/material/Grid';

const leftFeatures = [
    {
        icon: '/images/home/how-it-works_icon_1.png',
        title: 'Custom API Integration',
        description: 'Seamless integration with RESTful APIs, webhooks, and custom endpoints for maximum flexibility and control over your digital clone deployment'
    },
    {
        icon: '/images/home/how-it-works_icon_2.png',
        title: 'WordPress & Drupal Plugin',
        description: 'Quick 5-minute installation with native plugins for WordPress and Drupal. No coding required - just activate and configure your AI agent'
    },
    {
        icon: '/images/home/how-it-works_icon_3.png',
        title: 'LLM Open Connector',
        description: 'Advanced Einstein Studio integration allowing custom AI model endpoints that follow Open Connector API specifications for enterprise-grade solutions'
    }
];

const rightFeatures = [
    {
        icon: '/images/home/how-it-works_icon_1.png',
        title: 'Salesforce Native Integration',
        description: 'Complete Sales Cloud and Service Cloud integration with automated lead qualification, CRM data sync, and real-time customer insights'
    },
    {
        icon: '/images/home/how-it-works_icon_2.png',
        title: 'White-Label Customization',
        description: 'Fully brandable AI clones that preserve your personal brand identity with custom avatars, voice, personality, and visual styling options'
    },
    {
        icon: '/images/home/how-it-works_icon_3.png',
        title: 'Multi-Channel Deployment',
        description: 'Deploy across websites, mobile apps, XR applications, SMS, WhatsApp, and Messenger with unified conversation management and analytics'
    }
];

const integrationFeatures = [
    {
        title: 'Quick Website Integration',
        items: ['WordPress Plugin (5-min setup)', 'Drupal Module', 'HTML/JavaScript Embed', 'React/Vue Components']
    },
    {
        title: 'CRM & Business Systems',
        items: ['Salesforce Sales & Service Cloud', 'HubSpot Integration', 'Microsoft Dynamics', 'Custom CRM APIs']
    },
    {
        title: 'Communication Channels',
        items: ['Live Chat Widget', 'SMS & WhatsApp', 'Voice Integration', 'Email Automation']
    },
    {
        title: 'Advanced Customization',
        items: ['Custom Branding & UI', 'Personality Fine-tuning', 'Knowledge Base Training', '3D Avatar Customization']
    }
];

export default function Integration() {
    return (
        <Box
            id="integration"
            sx={{
                py: { xs: 4, md: 8 },
                background: 'linear-gradient(to bottom, #f8fafc 0%, #f0f7ff 50%, #ECF5FF 100%)'
            }}
        >
            <Container maxWidth="lg">
                <Typography
                    variant="h3"
                    fontWeight="bold"
                    sx={{
                        textAlign: 'center',
                        fontSize: { xs: '32px', md: '48px' },
                        color: '#1e293b',
                        mb: 2,
                        mx: { xs: 1, md: 20 },
                        lineHeight: 1.2
                    }}
                >
                    Seamless Integration & Full Customization
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        textAlign: 'center',
                        fontSize: { xs: '18px', md: '20px' },
                        color: '#64748b',
                        mb: { xs: 4, md: 6 },
                        mx: { xs: 2, md: 10 },
                        lineHeight: 1.6
                    }}
                >
                    From 5-minute WordPress setup to enterprise Salesforce integration - deploy your AI digital clone anywhere with powerful APIs and custom branding
                </Typography>

                <Grid container spacing={{ xs: 4, md: 4 }} alignItems="center">
                    <Grid size={{ xs: 12, md: 3 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {leftFeatures.map((feature, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            borderRadius: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={feature.icon}
                                            alt={feature.title}
                                            sx={{
                                                width: 60,
                                                height: 60
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{
                                                color: '#1e293b',
                                                mb: 1,
                                                fontSize: '18px'
                                            }}
                                        >
                                            {feature.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#64748b',
                                                fontSize: '16px',
                                                lineHeight: 1.5
                                            }}
                                        >
                                            {feature.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                mb: { xs: 4, md: 0 }
                            }}
                        >
                            <Box
                                component="img"
                                src="/images/home/how-it-works_mobile.png"
                                alt="Integration Options"
                                sx={{
                                    width: '100%',
                                    maxWidth: { xs: 300, md: 500 },
                                    height: 'auto'
                                }}
                            />
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {rightFeatures.map((feature, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            borderRadius: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={feature.icon}
                                            alt={feature.title}
                                            sx={{
                                                width: 60,
                                                height: 60
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{
                                                color: '#1e293b',
                                                mb: 1,
                                                fontSize: '18px'
                                            }}
                                        >
                                            {feature.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#64748b',
                                                fontSize: '16px',
                                                lineHeight: 1.5
                                            }}
                                        >
                                            {feature.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12 }} sx={{ display: { xs: 'block', md: 'none' }, mt: 0 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {[...leftFeatures, ...rightFeatures].map((feature, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            borderRadius: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={feature.icon}
                                            alt={feature.title}
                                            sx={{
                                                width: 60,
                                                height: 60
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{
                                                color: '#1e293b',
                                                mb: 1,
                                                fontSize: '18px'
                                            }}
                                        >
                                            {feature.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#64748b',
                                                fontSize: '16px',
                                                lineHeight: 1.5
                                            }}
                                        >
                                            {feature.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ mt: { xs: 6, md: 8 } }}>
                    <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{
                            textAlign: 'center',
                            fontSize: { xs: '30px', md: '35px' },
                            color: '#1e293b',
                            mb: { xs: 4, md: 5 }
                        }}
                    >
                        Integration Options & Capabilities
                    </Typography>

                    <Grid container spacing={{ xs: 2, md: 3 }}>
                        {integrationFeatures.map((category, index) => (
                            <Grid key={index} size={{ xs: 12, md: 6, lg: 3 }}>
                                <Box
                                    sx={{
                                        bgcolor: 'white',
                                        borderRadius: '16px',
                                        p: { xs: 3, md: 4 },
                                        height: '100%',
                                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                                        border: '1px solid #f1f5f9',
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
                                        }
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{
                                            color: '#1e293b',
                                            mb: 3,
                                            fontSize: '20px'
                                        }}
                                    >
                                        {category.title}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {category.items.map((item, itemIndex) => (
                                            <Box
                                                key={itemIndex}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        bgcolor: '#5C78FF',
                                                        flexShrink: 0
                                                    }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#64748b',
                                                        fontSize: '16px',
                                                        lineHeight: 1.4
                                                    }}
                                                >
                                                    {item}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Container>
        </Box>
    );
}